// ignore-string-externalization

import { contentfulGql } from '../../utils/contentfulGraphQL';
import { VIDEOS_QUERY } from './videosQuery';
import {
  formatVideoItem,
  formatVideoPageItem,
  normalize,
} from './videosTransformers';
import type {
  GetVideosQuery,
  GetVideosQueryVariables,
  VideoFieldsFragment,
  VideoPageFieldsFragment,
} from '../../graphql/generated';

function transformVideosResponse(
  data: GetVideosQuery,
  smartlingLanguage: string,
) {
  const section = data.sectionCollection?.items[0];
  if (!section) return null;

  const videosMap: Record<string, ReturnType<typeof formatVideoItem>> = {};
  const subSections = section.subSectionsCollection?.items ?? [];

  for (const cat of subSections) {
    if (!cat || !('subVideosCollection' in cat)) continue;
    for (const group of cat.subVideosCollection?.items ?? []) {
      if (!group || !('videosCollection' in group)) continue;
      for (const item of group.videosCollection?.items ?? []) {
        if (!item) continue;
        const formatted =
          item.__typename === 'Video'
            ? formatVideoItem(item as VideoFieldsFragment)
            : formatVideoPageItem(item as VideoPageFieldsFragment);
        if (formatted && !videosMap[formatted.id]) {
          videosMap[formatted.id] = formatted;
        }
      }
    }
  }

  const groupsMap: Record<string, any> = {};
  for (const cat of subSections) {
    if (!cat || !('subVideosCollection' in cat)) continue;
    for (const group of cat.subVideosCollection?.items ?? []) {
      if (!group || !('sys' in group)) continue;
      if (groupsMap[group.sys.id]) continue;
      const vids = group.aggregateOfAllVideos
        ? Object.keys(videosMap)
        : (group.videosCollection?.items ?? [])
            .filter((v: any) => v?.sys?.id && videosMap[v.sys.id])
            .map((v: any) => v.sys.id);

      groupsMap[group.sys.id] = {
        id: group.sys.id,
        name: group.name,
        body: group.body,
        aggregateOfAllVideos: group.aggregateOfAllVideos,
        urlSlug: group.urlSlug,
        videos: vids,
      };
    }
  }

  const categoriesWithVideos = subSections
    .filter(
      (cat): cat is Extract<typeof cat, { sys: any }> => !!cat && 'sys' in cat,
    )
    .map(cat => {
      const groups = (cat.subVideosCollection?.items ?? [])
        .filter((g): g is { sys: { id: string } } => !!g && 'sys' in g)
        .map(g => g.sys.id);
      const featuredVideoId =
        cat.featuredVideo && 'sys' in cat.featuredVideo
          ? cat.featuredVideo.sys.id
          : undefined;

      const videoSet = new Set<string>();
      for (const groupId of groups) {
        const group = groupsMap[groupId];
        if (group && !group.aggregateOfAllVideos) {
          group.videos.forEach((vid: string) => videoSet.add(vid));
        }
      }

      return {
        id: cat.sys.id,
        name: cat.name,
        body: cat.body,
        translated: cat.translated,
        urlSlug: cat.urlSlug,
        featuredVideo: videosMap[featuredVideoId ?? '']
          ? featuredVideoId
          : undefined,
        groups,
        videoSet,
      };
    });

  const filteredCategories = categoriesWithVideos.filter(c =>
    smartlingLanguage === 'en-US' ? true : c.translated,
  );

  const categories = filteredCategories.map(
    ({ videoSet, ...category }) => category,
  );

  const videos = Object.values(videosMap)
    .filter(Boolean)
    .map(video => ({
      ...video!,
      category: (
        categoriesWithVideos.find(cat => cat.videoSet.has(video!.id)) ?? {}
      ).id,
    }));

  return {
    videoCategories: normalize(categories),
    videos: normalize(videos),
    videoGroups: normalize(Object.values(groupsMap)),
    socialCard: section.socialCard?.url,
  };
}

export async function fetchVideosData({
  smartlingLanguage = 'en-US',
  preview = false,
} = {}) {
  const data = await contentfulGql<GetVideosQuery>(
    VIDEOS_QUERY,
    { locale: smartlingLanguage } satisfies GetVideosQueryVariables,
    preview,
  );

  return transformVideosResponse(data, smartlingLanguage);
}
